import {
  Box,
  SimpleGrid,
  Grid,
  GridItem,
  useColorModeValue,
  Text,
  Stack,
  Divider,
  Skeleton,
} from "@chakra-ui/react";
import React from "react";
import CheckTable from "views/user/default/components/CheckTable";
import TodayNotifications from "views/user/default/components/TodayNotifications";
import WeeklyReservations from "views/user/default/components/WeeklyReservations";
import NotificationPieChart from "views/user/default/components/NotificationPieChart";
import { Clock, Calendar, CheckCircle2, PauseCircle, Building, BedDouble } from "lucide-react";
import axios from "axios";
import { useEffect, useState } from "react";
import DOMPurify from 'dompurify';
import SimpleSidebar from "views/global/SimpleSidebar";
import { departmentLabels, taskStatusLabels, labelFor } from "lib/labels";

export default function UserReports() {
    // Chakra Color Mode
    const brandColor = useColorModeValue("brand.500", "white");
    const boxBg = useColorModeValue("secondaryGray.300", "whiteAlpha.100");

    const [cleaningData, setCleaningData] = useState([]);
    const [allTaskData, setAllTaskData] = useState([]);
    const [dashStats, setDashStats] = useState(null);
    const [loading, setLoading] = useState(true);

    const [isSidebarOpen, setSidebarOpen] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);


    useEffect(() => {
        const token = JSON.parse(localStorage.getItem("token"));
        const controller = new AbortController();
        setLoading(true);
        axios.get(`${process.env.REACT_APP_API}/user/dashboard/stats`, {
            signal: controller.signal,
            headers: { Authorization: `Bearer ${token}` },
        }).then(res => {
            setDashStats(res.data);
            setCleaningData(res.data.cleaning_tasks || []);
            setAllTaskData(res.data.operational_tasks || []);
        }).catch(err => {
            if (axios.isCancel(err)) return;
        }).finally(() => setLoading(false));
        return () => controller.abort();
    }, []);

    // Common columns split by position
    const commonStartColumns = [
        {
        Header: "Proprietà",
        accessor: "property",
        },
        {
        Header: "Appartamento",
        accessor: "apartment",
        },
    ];
    const commonEndColumns = [
        {
        Header: "Stato",
        accessor: "status",
        },
    ];
    // Cleaning and Task columns
    const cleaningColumn = [
        ...commonStartColumns,
        {
        Header: "Prenotazione",
        accessor: "reservation",
        },
        ...commonEndColumns,
    ];
    const taskColumn = [
        ...commonStartColumns,
        {
        Header: "Descrizione",
        accessor: "description",
        },
        ...commonEndColumns,
    ];

    const handleRowClick = (row) => {
        setSelectedTask(row);
        setSidebarOpen(true);
        console.log(row);
    };

    return (
        <Box>
            <SimpleGrid columns={{ base: 1, md: 1, xl: 2 }} gap='20px' mb='20px'>
                {loading ? (
                    <>
                        <Skeleton height="280px" borderRadius="16px" />
                        <Skeleton height="280px" borderRadius="16px" />
                    </>
                ) : (
                    <>
                        <CheckTable
                          columnsData={cleaningColumn}
                          tableData={cleaningData}
                          title="Pulizie del Giorno"
                          onRowClick={handleRowClick}
                        />
                        <CheckTable
                          columnsData={taskColumn}
                          tableData={allTaskData}
                          title="Attività Operative del Giorno"
                          onRowClick={handleRowClick}
                        />
                    </>
                )}
            </SimpleGrid>
            {loading ? (
                <Grid templateColumns={{ base: '1fr', xl: '2fr 1fr' }} gap="20px" mb="20px">
                    <Skeleton height="320px" borderRadius="16px" />
                    <Skeleton height="320px" borderRadius="16px" />
                </Grid>
            ) : dashStats && (
                <Grid
                    templateColumns={{ base: '1fr', xl: '2fr 1fr' }}
                    gap="20px"
                    mb="20px"
                >
                    <GridItem h="100%">
                        <TodayNotifications data={dashStats.today_notifications} />
                    </GridItem>
                    <GridItem h="100%">
                        <Stack direction="column" spacing="20px" h="100%">
                            <WeeklyReservations
                                labels={dashStats.weekly_reservations.labels}
                                data={dashStats.weekly_reservations.data}
                            />
                            <NotificationPieChart
                                acted={dashStats.notification_stats.acted}
                                pending={dashStats.notification_stats.pending}
                                total={dashStats.notification_stats.total}
                            />
                        </Stack>
                    </GridItem>
                </Grid>
            )}

            <SimpleSidebar
              status={isSidebarOpen}
              title={selectedTask?.description || selectedTask?.reservation || "Dettagli Attività"}
              viewOnly={true}
              cancel={{ text: "Chiudi", onClick: () => setSidebarOpen(false) }}
            >
              {selectedTask ? (
                <Box>
                    <Box
                        p={4}
                        borderRadius="md"
                        mb={4}
                        border={"1px solid"}
                        borderColor={"gray.200"}
                    >
                        <Box
                        mb={4}
                        className='task-header-meta'
                        display={{base:"block", md:"flex"}}
                        justifyContent={{base:"center", md:"space-between"}}
                        alignItems={{base:"center", md:"center"}}
                        >
                            {selectedTask.data.assigned_at &&
                            <Text className='task-header-created-date task-header-item'>
                                <Clock/>
                                {selectedTask.data.assigned_at}
                            </Text>
                            }
                            {selectedTask.data.due_date &&
                            <Text className="task-header-due-date task-header-item">
                                <Calendar/>
                                {selectedTask.data.due_date}
                            </Text>
                            }
                            {selectedTask.data.status &&
                            <Text className={selectedTask.data.status+ ' task-header-status task-header-item'}>
                                {selectedTask.data.status==="completed" ? <CheckCircle2/> : <PauseCircle/>}
                                {selectedTask.data.status==="completed" ? selectedTask.data.completed_at : labelFor(taskStatusLabels, selectedTask.data.status)}
                            </Text>
                            }
                        </Box>
                        <Box
                        className='task-header-meta'
                        display={{base:"block", md:"flex"}}
                        alignItems={{base:"center", md:"center"}}
                        gap={4}
                        >
                            {selectedTask.data.property &&
                            <Text className='task-header-property task-header-item'>
                                <Building/>
                                {selectedTask.data.property.identifier}
                            </Text>
                            }
                            {selectedTask.data.reservation?.property_room && (
                                <Text className="task-header-property task-header-item">
                                    <BedDouble />
                                    {selectedTask.data.reservation.property_room.description}
                                </Text>
                            )}
                        </Box>
                    </Box>
                    {selectedTask.data.reservation && (
                        <Box
                        mb={4}
                        className='task-header-meta wr-info-card'
                        display={{base:"block", md:"flex"}}
                        alignItems={{base:"center", md:"center"}}
                        borderWidth="1px"
                        borderColor="gray.200"
                        borderRadius={5}
                        padding={4}
                        >   
                            <>
                            <Text className='wr-info-card-header'>Prenotazione</Text>
                            <Text className="task-header-assignee  task-header-item" me={5}>
                                Nome: <span>{selectedTask.data.reservation.name}</span>
                            </Text>
                            <Text className="task-header-assignee  task-header-item" me={5}>
                                ID: <span>{selectedTask.data.reservation.id ? selectedTask.data.reservation.id : "-"}</span>
                            </Text>
                            </>  
                        </Box>
                    )}
                    <Box
                        mb={4}
                        className='task-header-meta wr-info-card'
                        display={{base:"block", md:"flex"}}
                        alignItems={{base:"center", md:"center"}}
                        borderWidth="1px"
                        borderColor="gray.200"
                        borderRadius={5}
                        padding={4}
                        >
                            <Text className='wr-info-card-header'>Gestione</Text>
                            <Text className="task-header-assignee  task-header-item" me={5}>
                                Creata da: <span>{selectedTask.data.user ? selectedTask.data.user.name : "Admin"}</span>
                            </Text>
                            {selectedTask.data.assignee && (
                                <Text className="task-header-assignee  task-header-item">
                                    Assegnata a: <span> {selectedTask.data.assignee.name}</span>
                                </Text>
                            )}
                            {selectedTask.data.department && (
                                <Text className="task-header-assignee  task-header-item">
                                    Reparto: <span> {labelFor(departmentLabels, selectedTask.data.department)}</span>
                                </Text>
                            )}
                    </Box>
                    <Text
                    mb={4}
                    >
                        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedTask.data.description) }} />
                    </Text>
                </Box>
              ) : (
                <Text>Nessun elemento selezionato.</Text>
              )}
            </SimpleSidebar>
        </Box>
    );
}
